import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Diamond, UserCheck, UserX, ExternalLink, KeyRound, Pencil, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";

export default function AdminResellers() {
  const [list, setList] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openAllocate, setOpenAllocate] = useState(null);
  const [openDeduct, setOpenDeduct] = useState(null);
  const [openEdit, setOpenEdit] = useState(null);
  const [openPwd, setOpenPwd] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = async () => {
    const { data } = await api.get("/admin/resellers");
    setList(data);
  };

  useEffect(() => { refresh(); }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/resellers/${deleteTarget.id}`);
      toast.success("Reseller deleted");
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-resellers-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Network</p>
          <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Resellers</h1>
          <p className="text-zinc-500 mt-1">Create, allocate diamonds to, and audit your reseller network.</p>
        </div>
        <Button
          onClick={() => setOpenCreate(true)}
          className="bg-zinc-900 hover:bg-zinc-800 text-white"
          data-testid="add-reseller-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Reseller
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Name</th>
              <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Email</th>
              <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Phone</th>
              <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">City / State</th>
              <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Status</th>
              <th className="text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Allocated</th>
              <th className="text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Balance</th>
              <th className="text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-sm text-zinc-400">
                  No resellers yet. Click &quot;Add Reseller&quot; to create one.
                </td>
              </tr>
            )}
            {list.map((r) => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`reseller-row-${r.id}`}>
                <td className="px-6 py-4 text-sm font-medium text-zinc-900">{r.name || "—"}</td>
                <td className="px-6 py-4 text-sm text-zinc-700">{r.email}</td>
                <td className="px-6 py-4 text-sm text-zinc-700 font-mono">{r.phone || "—"}</td>
                <td className="px-6 py-4 text-sm text-zinc-700">
                  {[r.city, r.state].filter(Boolean).join(", ") || "—"}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    r.status === "active" ? "bg-green-100 text-green-800" : "bg-zinc-200 text-zinc-700"
                  }`}>
                    {r.status === "active" ? (<><UserCheck className="w-3 h-3 mr-1" />Active</>) : (<><UserX className="w-3 h-3 mr-1" />Disabled</>)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-zinc-900 text-right tabular-nums font-mono">{r.diamonds_allocated.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm font-semibold text-zinc-900 text-right tabular-nums font-mono">{r.diamonds_balance.toLocaleString()}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => setOpenAllocate(r)} data-testid={`allocate-button-${r.id}`}>
                      <Diamond className="w-3.5 h-3.5 mr-1" /> Allocate
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setOpenDeduct(r)} className="text-amber-700 hover:text-amber-800 hover:bg-amber-50" data-testid={`deduct-button-${r.id}`}>
                      <MinusCircle className="w-3.5 h-3.5 mr-1" /> Take Back
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setOpenEdit(r)} data-testid={`edit-reseller-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setOpenPwd(r)} data-testid={`reset-password-${r.id}`}>
                      <KeyRound className="w-3.5 h-3.5" />
                    </Button>
                    <Link to={`/admin/resellers/${r.id}`}>
                      <Button size="sm" variant="outline" data-testid={`view-reseller-${r.id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeleteTarget(r)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-reseller-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateResellerDialog open={openCreate} onClose={() => setOpenCreate(false)} onSaved={refresh} />
      <EditResellerDialog reseller={openEdit} onClose={() => setOpenEdit(null)} onSaved={refresh} />
      <ResetPasswordDialog reseller={openPwd} onClose={() => setOpenPwd(null)} />
      <AllocateDialog reseller={openAllocate} onClose={() => setOpenAllocate(null)} onAllocated={refresh} />
      <DeductDialog reseller={openDeduct} onClose={() => setOpenDeduct(null)} onDeducted={refresh} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete reseller?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deleteTarget?.email}</strong>, including their allocations and recharge history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-reseller"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateResellerDialog({ open, onClose, onSaved }) {
  const empty = { name: "", email: "", password: "", phone: "", city: "", state: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/resellers", form);
      toast.success("Reseller created");
      setForm(empty);
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-reseller-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-display">Add reseller</DialogTitle>
            <DialogDescription>Create login credentials. Share them with the reseller manually.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Field label="Full name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="create-name" />
            </Field>
            <Field label="Email (login)">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="create-email" />
            </Field>
            <Field label="Password">
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="create-password" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="create-phone" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City">
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="create-city" />
              </Field>
              <Field label="State">
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="create-state" />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" disabled={loading} data-testid="submit-create-reseller">
              {loading ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditResellerDialog({ reseller, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", city: "", state: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (reseller) {
      setForm({
        name: reseller.name || "",
        phone: reseller.phone || "",
        city: reseller.city || "",
        state: reseller.state || "",
      });
    }
  }, [reseller]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/admin/resellers/${reseller.id}`, form);
      toast.success("Reseller updated");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={!!reseller} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-reseller-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-display">Edit reseller</DialogTitle>
            <DialogDescription>Update <strong>{reseller?.email}</strong>&apos;s profile.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Field label="Full name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="edit-name" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City">
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="edit-city" />
              </Field>
              <Field label="State">
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="edit-state" />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" disabled={loading} data-testid="submit-edit-reseller">
              {loading ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ reseller, onClose }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { setPassword(""); }, [reseller]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/admin/resellers/${reseller.id}`, { password });
      toast.success("Password reset");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={!!reseller} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-password-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-display">Reset password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{reseller?.email}</strong>. Share it with them manually.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Field label="New password">
              <Input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Min 6 characters"
                data-testid="new-password-input"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" disabled={loading || password.length < 6} data-testid="submit-reset-password">
              {loading ? "Saving…" : "Set password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AllocateDialog({ reseller, onClose, onAllocated }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [platform, setPlatform] = useState("trumeet");
  const [loading, setLoading] = useState(false);
  const platforms = reseller?.balances
    ? Object.entries(reseller.balances).map(([key, v]) => ({ key, ...v }))
    : [{ key: "trumeet", name: "Trumeet", balance: reseller?.diamonds_balance ?? 0 }];
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/allocations", {
        reseller_id: reseller.id,
        amount: parseInt(amount, 10),
        note,
        platform,
      });
      toast.success(`Allocated ${amount} diamonds to ${reseller.email} (${platform})`);
      setAmount("");
      setNote("");
      onClose();
      onAllocated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={!!reseller} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="allocate-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-display">Allocate diamonds</DialogTitle>
            <DialogDescription>
              Add diamonds to <strong>{reseller?.email}</strong>&apos;s balance for the selected platform.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Field label="Platform">
              <div className="flex flex-wrap gap-2" data-testid="allocate-platform-selector">
                {platforms.map((p) => (
                  <button
                    type="button"
                    key={p.key}
                    onClick={() => setPlatform(p.key)}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                      platform === p.key
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                    }`}
                    data-testid={`allocate-platform-${p.key}`}
                  >
                    {p.name || p.key}
                    <span className="ml-1.5 text-xs opacity-70 font-mono">({(p.balance ?? 0).toLocaleString()})</span>
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Amount (diamonds)">
              <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="allocate-amount" />
            </Field>
            <Field label="Note (optional)">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Monthly refill" data-testid="allocate-note" />
            </Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" disabled={loading} data-testid="submit-allocate">
              {loading ? "Allocating…" : "Allocate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeductDialog({ reseller, onClose, onDeducted }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [platform, setPlatform] = useState("trumeet");
  const [loading, setLoading] = useState(false);
  const platforms = reseller?.balances
    ? Object.entries(reseller.balances).map(([key, v]) => ({ key, ...v }))
    : [{ key: "trumeet", name: "Trumeet", balance: reseller?.diamonds_balance ?? 0 }];
  const selected = platforms.find((p) => p.key === platform) || platforms[0];
  const available = selected?.balance ?? 0;
  const amt = parseInt(amount, 10) || 0;
  const overLimit = amt > available;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/allocations/deduct", {
        reseller_id: reseller.id,
        amount: amt,
        note,
        platform,
      });
      toast.success(`Took back ${amt} diamonds from ${reseller.email} (${platform})`);
      setAmount("");
      setNote("");
      onClose();
      onDeducted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={!!reseller} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="deduct-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-display">Take back diamonds</DialogTitle>
            <DialogDescription>
              Reclaim diamonds from <strong>{reseller?.email}</strong>&apos;s stock for the selected platform. You can take back at most their available balance.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Field label="Platform">
              <div className="flex flex-wrap gap-2" data-testid="deduct-platform-selector">
                {platforms.map((p) => (
                  <button
                    type="button"
                    key={p.key}
                    onClick={() => setPlatform(p.key)}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                      platform === p.key
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                    }`}
                    data-testid={`deduct-platform-${p.key}`}
                  >
                    {p.name || p.key}
                    <span className="ml-1.5 text-xs opacity-70 font-mono">({(p.balance ?? 0).toLocaleString()})</span>
                  </button>
                ))}
              </div>
            </Field>
            <Field label={`Amount (max ${available.toLocaleString()})`}>
              <Input type="number" min="1" max={available} value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="deduct-amount" />
              {overLimit && (
                <p className="text-xs text-red-600" data-testid="deduct-over-limit">
                  Exceeds available balance of {available.toLocaleString()}.
                </p>
              )}
            </Field>
            <Field label="Note (optional)">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Reallocation / refund" data-testid="deduct-note" />
            </Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={loading || amt < 1 || overLimit} data-testid="submit-deduct">
              {loading ? "Processing…" : "Take Back"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="grid gap-2">
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>
      {children}
    </div>
  );
}
